import { useState } from 'react';
import { X } from 'lucide-react';
import type { EmployeeRecordT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { SUPPORTED_PRACTICE_AREAS } from '@/shared/lib/practiceAreas';
import { getUsStateNameByCode } from '@/shared/lib/usStates';
import { Alert, AlertDescription } from '@/shared/ui/shadcn/alert';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/shadcn/command';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/shared/ui/shadcn/field';
import { Input } from '@/shared/ui/shadcn/input';
import { StateMultiSelect } from '@/shared/ui/primitives';

import type { EmployeeRecordPayload } from '../../model/useClinicianOnboardingOrchestrator';
import { DS_STEP_FORM_ID } from './_shared/dsStepFormId';
import {
  normalizeValidatedPracticeAreas,
  normalizeStateSelections,
  type FillEmployeeRecordFormErrors,
  validateFillEmployeeRecordForm,
} from './fillEmployeeRecordValidation';

interface FillEmployeeRecordStepProps {
  onSubmit: (payload: EmployeeRecordPayload) => void;
  isSubmitting: boolean;
  submitError: Error | null;
  onResetError: () => void;
  prefill: EmployeeRecordT;
}

export function FillEmployeeRecordStep({
  onSubmit,
  isSubmitting,
  submitError,
  onResetError,
  prefill,
}: FillEmployeeRecordStepProps) {
  const [displayNameDraft, setDisplayNameDraft] = useState<string | null>(null);
  const [emailDraft, setEmailDraft] = useState<string | null>(null);
  const [licensedStatesDraft, setLicensedStatesDraft] = useState<string[] | null>(null);
  const [licensedPracticeAreasDraft, setLicensedPracticeAreasDraft] = useState<string[] | null>(
    null
  );
  const [practiceAreasSearch, setPracticeAreasSearch] = useState('');
  const [practiceAreasDropdownOpen, setPracticeAreasDropdownOpen] = useState(false);
  const [errors, setErrors] = useState<FillEmployeeRecordFormErrors>({
    displayName: null,
    email: null,
    licensedStates: null,
    licensedPracticeAreas: null,
  });

  const prefilledDisplayName = prefill.displayName ?? '';
  const prefilledEmail = prefill.email ?? '';
  const prefilledLicensedStates = normalizeStateSelections(prefill.licensedStates ?? []);
  const prefilledLicensedPracticeAreas = prefill.licensedPracticeAreas ?? [];
  const displayName = displayNameDraft ?? prefilledDisplayName;
  const email = emailDraft ?? prefilledEmail;
  const licensedStates = licensedStatesDraft ?? prefilledLicensedStates;
  const selectedPracticeAreas = licensedPracticeAreasDraft ?? prefilledLicensedPracticeAreas;
  const licensedPracticeAreas = selectedPracticeAreas.join(', ');

  const normalizedStates = normalizeStateSelections(licensedStates);
  const normalizedPracticeAreas = normalizeValidatedPracticeAreas(licensedPracticeAreas);
  const practiceAreasSearchTerm = practiceAreasSearch.trim().toLowerCase();
  const filteredPracticeAreas = SUPPORTED_PRACTICE_AREAS.filter((area) => {
    if (practiceAreasSearchTerm === '') {
      return true;
    }

    return area.toLowerCase().includes(practiceAreasSearchTerm);
  });
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const nextErrors = validateFillEmployeeRecordForm({
      displayName,
      email,
      licensedStates: normalizedStates,
      licensedPracticeAreas,
    });
    setErrors(nextErrors);
    if (
      isSubmitting ||
      nextErrors.displayName !== null ||
      nextErrors.email !== null ||
      nextErrors.licensedStates !== null ||
      nextErrors.licensedPracticeAreas !== null
    ) {
      return;
    }

    onResetError();
    onSubmit({
      displayName: displayName.trim(),
      email: email.trim(),
      licensedStates: normalizedStates
        .map((stateCode) => getUsStateNameByCode(stateCode))
        .filter((stateName): stateName is string => stateName !== null),
      licensedPracticeAreas: normalizedPracticeAreas,
    });
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <form id={DS_STEP_FORM_ID} onSubmit={handleSubmit} className="flex w-full flex-col gap-4">
        <FieldGroup>
          <Field data-invalid={errors.displayName !== null}>
            <FieldLabel htmlFor="displayName">Full name</FieldLabel>
            <Input
              id="displayName"
              type="text"
              placeholder="Jane Smith"
              value={displayName}
              onChange={(e) => {
                setDisplayNameDraft(e.currentTarget.value);
                setErrors((prev) => ({
                  ...prev,
                  displayName: null,
                }));
              }}
              disabled={isSubmitting}
              autoFocus
              aria-invalid={errors.displayName !== null}
              aria-describedby={errors.displayName !== null ? 'displayName-error' : undefined}
              required
            />
            {errors.displayName !== null ? (
              <FieldError id="displayName-error">{errors.displayName}</FieldError>
            ) : null}
          </Field>

          <Field data-invalid={errors.email !== null}>
            <FieldLabel htmlFor="email">Email</FieldLabel>
            <Input
              id="email"
              type="email"
              placeholder="jane@lotus.ai"
              value={email}
              onChange={(e) => {
                setEmailDraft(e.currentTarget.value);
                setErrors((prev) => ({
                  ...prev,
                  email: null,
                }));
              }}
              disabled={isSubmitting}
              aria-invalid={errors.email !== null}
              aria-describedby={errors.email !== null ? 'email-error' : undefined}
              required
            />
            {errors.email !== null ? (
              <FieldError id="email-error">{errors.email}</FieldError>
            ) : null}
          </Field>

          <StateMultiSelect
            value={normalizedStates}
            onChange={(codes) => {
              setLicensedStatesDraft(codes);
              setErrors((prev) => ({ ...prev, licensedStates: null }));
            }}
            disabled={isSubmitting}
            error={errors.licensedStates}
          />

          <Field data-invalid={errors.licensedPracticeAreas !== null}>
            <FieldLabel htmlFor="licensedPracticeAreas">Practice areas</FieldLabel>
            <Command
              className="relative overflow-visible bg-transparent [&_[data-slot=command-input-wrapper]]:h-9 [&_[data-slot=command-input-wrapper]]:rounded-full [&_[data-slot=command-input-wrapper]]:border [&_[data-slot=command-input-wrapper]]:border-input [&_[data-slot=command-input-wrapper]]:border-b-input [&_[data-slot=command-input-wrapper]]:px-4 [&_[data-slot=command-list]]:absolute [&_[data-slot=command-list]]:inset-x-0 [&_[data-slot=command-list]]:top-full [&_[data-slot=command-list]]:z-10 [&_[data-slot=command-list]]:mt-1 [&_[data-slot=command-list]]:rounded-md [&_[data-slot=command-list]]:border [&_[data-slot=command-list]]:bg-popover [&_[data-slot=command-list]]:shadow-md"
              shouldFilter={false}
            >
              <CommandInput
                id="licensedPracticeAreas"
                value={practiceAreasSearch}
                onValueChange={(nextValue) => {
                  setPracticeAreasSearch(nextValue);
                  setPracticeAreasDropdownOpen(true);
                }}
                onFocus={() => setPracticeAreasDropdownOpen(true)}
                onBlur={() => {
                  setTimeout(() => setPracticeAreasDropdownOpen(false), 150);
                }}
                placeholder="Search practice areas"
                aria-invalid={errors.licensedPracticeAreas !== null}
                aria-describedby={
                  errors.licensedPracticeAreas !== null ? 'licensedPracticeAreas-error' : undefined
                }
                disabled={isSubmitting}
              />
              {practiceAreasDropdownOpen ? (
                <CommandList>
                  {filteredPracticeAreas.length === 0 ? (
                    <CommandEmpty>No matching practice areas found.</CommandEmpty>
                  ) : (
                    <CommandGroup>
                      {filteredPracticeAreas.map((area) => {
                        const isSelected = selectedPracticeAreas.includes(area);
                        return (
                          <CommandItem
                            key={area}
                            value={area}
                            onSelect={() => {
                              setLicensedPracticeAreasDraft((prev) => {
                                const current = prev ?? [...selectedPracticeAreas];
                                if (current.includes(area)) {
                                  return current.filter((item) => item !== area);
                                }

                                return [...current, area];
                              });
                              setErrors((prev) => ({
                                ...prev,
                                licensedPracticeAreas: null,
                              }));
                            }}
                          >
                            <span className="flex-1 text-sm">{area}</span>
                            {isSelected ? (
                              <span className="text-xs text-muted-foreground">Selected</span>
                            ) : null}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  )}
                </CommandList>
              ) : null}
            </Command>
            {selectedPracticeAreas.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {selectedPracticeAreas.map((area) => (
                  <Badge key={area} variant="secondary" className="gap-1 pr-1">
                    <span className="text-xs">{area}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="ml-0.5 size-4 p-0 hover:bg-transparent"
                      onClick={() => {
                        setLicensedPracticeAreasDraft((prev) => {
                          const current = prev ?? [...selectedPracticeAreas];
                          return current.filter((item) => item !== area);
                        });
                      }}
                      aria-label={`Remove ${area}`}
                      disabled={isSubmitting}
                    >
                      <X className="size-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            ) : null}
            {errors.licensedPracticeAreas !== null ? (
              <FieldError id="licensedPracticeAreas-error">
                {errors.licensedPracticeAreas}
              </FieldError>
            ) : (
              <FieldDescription>Select one or more practice areas.</FieldDescription>
            )}
          </Field>
        </FieldGroup>

        {submitError ? (
          <Alert variant="destructive">
            <AlertDescription>{submitError.message}</AlertDescription>
          </Alert>
        ) : null}
      </form>
    </div>
  );
}
